/**
 * TcfApi — Implements the IAB TCF 2.2 __tcfapi CMP API.
 *
 * Installs window.__tcfapi and the __tcfapiLocator iframe for cross-frame
 * postMessage-based discovery per the TCF specification.
 */

import type { TCData, PingReturn, TcfApiCallback, BooleanMap } from "./TcfTypes";
import { TcStringEncoder, type TcStringParams } from "./TcString";

export interface TcfApiOptions {
  cmpId: number;
  cmpVersion: number;
  gdprApplies: boolean;
}

interface TcfListener {
  id: number;
  callback: TcfApiCallback;
}

declare global {
  interface Window {
    __tcfapi?: (command: string, version: number, callback: TcfApiCallback, parameter?: unknown) => void;
  }
}

export class TcfApi {
  private cmpId: number;
  private cmpVersion: number;
  private gdprApplies: boolean;
  private encoder = new TcStringEncoder();

  private cmpStatus: "loading" | "loaded" | "error" = "loading";
  private displayStatus: "visible" | "hidden" | "disabled" = "visible";
  private eventStatus: "cmpuishown" | "tcloaded" | "useractioncomplete" | null = null;

  private tcString: string = "";
  private purposeConsents: BooleanMap = {};
  private purposeLI: BooleanMap = {};
  private vendorConsents: BooleanMap = {};
  private vendorLI: BooleanMap = {};
  private specialFeatureOptins: BooleanMap = {};
  private publisherCC: string = "AA";
  private vendorListVersion: number = 0;

  private listeners: TcfListener[] = [];
  private nextListenerId = 1;
  private locatorFrame: HTMLIFrameElement | null = null;
  private messageHandler: ((e: MessageEvent) => void) | null = null;

  constructor(options: TcfApiOptions) {
    this.cmpId = options.cmpId;
    this.cmpVersion = options.cmpVersion;
    this.gdprApplies = options.gdprApplies;
  }

  /**
   * Install __tcfapi on window and create __tcfapiLocator iframe.
   * Call with hasExistingConsent=true to skip the "cmpuishown" state.
   */
  install(hasExistingConsent: boolean = false): void {
    this.cmpStatus = "loaded";

    if (hasExistingConsent) {
      this.eventStatus = "tcloaded";
      this.displayStatus = "disabled";
    } else {
      this.eventStatus = "cmpuishown";
      this.displayStatus = "visible";
    }

    // Capture any commands the synchronous stub queued (in `.a`) BEFORE we
    // overwrite window.__tcfapi. Reading the queue after the overwrite (as
    // before) always saw an empty queue and dropped every pre-load vendor call.
    const stub = window.__tcfapi as unknown as { a?: unknown[][] } | undefined;
    const queued: unknown[][] = Array.isArray(stub?.a) ? stub!.a!.slice() : [];

    // Install the real __tcfapi
    window.__tcfapi = (command: string, version: number, callback: TcfApiCallback, parameter?: unknown) => {
      this.handleCommand(command, version, callback, parameter);
    };

    // Create __tcfapiLocator iframe for cross-frame discovery
    this.createLocatorFrame();

    // Listen for postMessage-based cross-frame __tcfapi calls
    this.messageHandler = (event: MessageEvent) => {
      this.handlePostMessage(event);
    };
    window.addEventListener("message", this.messageHandler);

    // Drain the commands the stub captured before we loaded
    this.drainQueue(queued);
  }

  /**
   * Update TC data when user makes consent decisions.
   */
  updateConsent(params: {
    purposeConsents: Set<number>;
    purposeLegitimateInterests: Set<number>;
    vendorConsents: Set<number>;
    vendorLegitimateInterests: Set<number>;
    specialFeatureOptins: Set<number>;
    vendorListVersion: number;
    consentLanguage: string;
    publisherCC: string;
  }, isUserAction: boolean = true): void {
    // Build BooleanMaps
    this.purposeConsents = setToBooleanMap(params.purposeConsents, 24);
    this.purposeLI = setToBooleanMap(params.purposeLegitimateInterests, 24);
    this.vendorConsents = setToBooleanMapSparse(params.vendorConsents);
    this.vendorLI = setToBooleanMapSparse(params.vendorLegitimateInterests);
    this.specialFeatureOptins = setToBooleanMap(params.specialFeatureOptins, 12);
    this.vendorListVersion = params.vendorListVersion;
    this.publisherCC = params.publisherCC;

    // Encode TC string
    const encodeParams: TcStringParams = {
      cmpId: this.cmpId,
      cmpVersion: this.cmpVersion,
      consentLanguage: params.consentLanguage,
      vendorListVersion: params.vendorListVersion,
      purposeConsents: params.purposeConsents,
      purposeLegitimateInterests: params.purposeLegitimateInterests,
      specialFeatureOptins: params.specialFeatureOptins,
      vendorConsents: params.vendorConsents,
      vendorLegitimateInterests: params.vendorLegitimateInterests,
      publisherCC: params.publisherCC,
    };
    this.tcString = this.encoder.encode(encodeParams);

    // Update state. After an explicit user action the spec requires
    // "useractioncomplete"; "tcloaded" is only for restoring a prior string.
    this.eventStatus = isUserAction ? "useractioncomplete" : "tcloaded";
    this.displayStatus = "hidden";

    // Notify all listeners
    this.notifyListeners();
  }

  /** Get the current TC string */
  getTcString(): string {
    return this.tcString;
  }

  /** Clean up — remove __tcfapi, locator frame, and message listener */
  destroy(): void {
    delete window.__tcfapi;
    this.locatorFrame?.remove();
    this.locatorFrame = null;
    if (this.messageHandler) {
      window.removeEventListener("message", this.messageHandler);
      this.messageHandler = null;
    }
    this.listeners = [];
  }

  // ─── Private ───────────────────────────────────────────────────────

  private handleCommand(command: string, _version: number, callback: TcfApiCallback, parameter?: unknown): void {
    switch (command) {
      case "getTCData":
        callback(this.buildTCData(), true);
        break;

      case "ping":
        callback(this.buildPing(), true);
        break;

      case "addEventListener": {
        const id = this.nextListenerId++;
        this.listeners.push({ id, callback });
        // Immediately call with current state
        const tcData = this.buildTCData();
        tcData.listenerId = id;
        callback(tcData, true);
        break;
      }

      case "removeEventListener": {
        const listenerId = parameter as number;
        const idx = this.listeners.findIndex((l) => l.id === listenerId);
        if (idx >= 0) {
          this.listeners.splice(idx, 1);
          callback(true as unknown as TCData, true);
        } else {
          callback(null, false);
        }
        break;
      }

      default:
        callback(null, false);
    }
  }

  private buildTCData(): TCData {
    return {
      tcString: this.tcString,
      tcfPolicyVersion: 5,
      cmpId: this.cmpId,
      cmpVersion: this.cmpVersion,
      gdprApplies: this.gdprApplies,
      eventStatus: this.eventStatus,
      cmpStatus: this.cmpStatus,
      isServiceSpecific: true,
      useNonStandardTexts: false,
      publisherCC: this.publisherCC,
      purposeOneTreatment: false,
      purpose: {
        consents: { ...this.purposeConsents },
        legitimateInterests: { ...this.purposeLI },
      },
      vendor: {
        consents: { ...this.vendorConsents },
        legitimateInterests: { ...this.vendorLI },
      },
      specialFeatureOptins: { ...this.specialFeatureOptins },
      publisher: {
        consents: {},
        legitimateInterests: {},
        customPurpose: {
          consents: {},
          legitimateInterests: {},
        },
        restrictions: {},
      },
    };
  }

  private buildPing(): PingReturn {
    return {
      gdprApplies: this.gdprApplies,
      cmpLoaded: this.cmpStatus === "loaded",
      cmpStatus: this.cmpStatus,
      displayStatus: this.displayStatus,
      apiVersion: "2.2",
      cmpVersion: this.cmpVersion,
      cmpId: this.cmpId,
      gvlVersion: this.vendorListVersion,
      tcfPolicyVersion: 5,
    };
  }

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      const tcData = this.buildTCData();
      tcData.listenerId = listener.id;
      try {
        listener.callback(tcData, true);
      } catch {
        // Don't let listener errors break the CMP
      }
    }
  }

  private createLocatorFrame(): void {
    if (document.getElementById("__tcfapiLocator")) return;
    const frame = document.createElement("iframe");
    frame.style.cssText = "display:none;position:absolute;width:0;height:0;";
    frame.name = "__tcfapiLocator";
    frame.id = "__tcfapiLocator";
    frame.setAttribute("tabindex", "-1");
    frame.setAttribute("aria-hidden", "true");
    document.body.appendChild(frame);
    this.locatorFrame = frame;
  }

  /** Handle postMessage-based __tcfapi calls from cross-frame contexts */
  private handlePostMessage(event: MessageEvent): void {
    let msg: { __tcfapiCall?: { command: string; version: number; callId: string; parameter?: unknown } };
    try {
      msg = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
    } catch {
      return;
    }
    if (!msg?.__tcfapiCall) return;

    const { command, version, callId, parameter } = msg.__tcfapiCall;
    this.handleCommand(command, version, (tcData, success) => {
      const response = {
        __tcfapiReturn: {
          returnValue: tcData,
          success,
          callId,
        },
      };
      if (event.source) {
        (event.source as WindowProxy).postMessage(response, "*");
      }
    }, parameter);
  }

  /** Replay the commands a CMP stub queued before we loaded */
  private drainQueue(queue: unknown[][]): void {
    for (const args of queue) {
      if (args.length >= 3) {
        this.handleCommand(
          args[0] as string,
          args[1] as number,
          args[2] as TcfApiCallback,
          args[3] as unknown
        );
      }
    }
  }
}

// ─── Helpers ───────────────────────────────────────────────────────────

/** Convert a Set of IDs to a BooleanMap for IDs 1..max */
function setToBooleanMap(ids: Set<number>, max: number): BooleanMap {
  const map: BooleanMap = {};
  for (let i = 1; i <= max; i++) {
    map[i] = ids.has(i);
  }
  return map;
}

/** Convert a sparse Set of vendor IDs to a BooleanMap (only set IDs present) */
function setToBooleanMapSparse(ids: Set<number>): BooleanMap {
  const map: BooleanMap = {};
  ids.forEach((id) => {
    map[id] = true;
  });
  return map;
}
